import { captureMessage } from "@sentry/remix";
import { ShirtspaceResponse } from "@services/shirtspace.types";
import uniqBy from "lodash/uniqBy";
import { Suspense, useRef } from "react";
import { Await } from "react-router-dom";
import EmptyProductQuantitySelector from "~/components/ProductQuantitySelector/Empty";
import ProductQuantitySelector, {
  InventoryPriceVariant,
} from "~/components/ProductQuantitySelector/ProductQuantitySelector";
import { Product, ProductAvailability } from "~/entities/product/types";
import { PriceVariant, Variant } from "~/entities/variant/types";
import { sortBySize } from "~/entities/variant/utils";

export const inventoryPriceVariantsFrom = (
  product: Product,
  productAvailability: ProductAvailability,
): InventoryPriceVariant[] => {
  const variantsById: Record<string, Variant> = product.variants.reduce(
    (acc, curr) => ({ ...acc, [curr.id]: curr }),
    {},
  );

  const priceVariantsById: Record<string, PriceVariant> =
    productAvailability.prices.reduce(
      (acc, curr) => ({ ...acc, [curr.id]: curr }),
      {},
    );

  return productAvailability.inventories
    .reduce((acc, inventoryVariant) => {
      const variant = variantsById[inventoryVariant.id];
      const priceVariant = priceVariantsById[inventoryVariant.id];

      if (!variant || !priceVariant) {
        captureMessage("Cannot find variant from inventories and prices", {
          extra: {
            productId: product.id,
            variantId: inventoryVariant.id,
            productAvailability,
          },
        });

        return acc;
      }

      return [
        ...acc,
        {
          ...inventoryVariant,
          ...priceVariant,
          ...variant,
        },
      ];
    }, [] as InventoryPriceVariant[])
    .sort(sortBySize);
};

const uniqueSortedSizesFrom = (product: Product): string[] => {
  return uniqBy(product.variants, "size")
    .sort(sortBySize)
    .flatMap((v) => v.size ?? []);
};

type Props = {
  productAvailability?: Promise<ShirtspaceResponse<ProductAvailability>>;
  product: Product;
};

const Container: React.FC<Props> = ({ product, productAvailability }) => {
  const fallback = useRef<React.ReactNode>();
  const emptyFallback = useRef<React.ReactNode>(
    <EmptyProductQuantitySelector sizes={uniqueSortedSizesFrom(product)} />,
  );

  return (
    <Suspense fallback={fallback.current ?? emptyFallback.current}>
      <Await
        errorElement={fallback.current ?? emptyFallback.current}
        resolve={productAvailability}
      >
        {(response: ShirtspaceResponse<ProductAvailability> | undefined) => {
          if (!response?.data) {
            return fallback.current ?? emptyFallback.current;
          }

          fallback.current = (
            <ProductQuantitySelector
              inventoryPriceVariants={inventoryPriceVariantsFrom(
                product,
                response.data,
              )}
              isLoading={true}
            />
          );

          return (
            <ProductQuantitySelector
              inventoryPriceVariants={inventoryPriceVariantsFrom(
                product,
                response.data,
              )}
            />
          );
        }}
      </Await>
    </Suspense>
  );
};

export default Container;
